'use client';
import { Credits } from '@/components/Credits';
import { useCatalog } from '../hooks/useCatalog';
import { useMyAchievements } from '../hooks/useMyAchievements';
import { AchievementStatsNotFoundError, useAchievementStats } from '../hooks/useAchievementStats';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { useOpenClaimSheet } from '@/stores/claimSheetStore';
import translations from '../AchievementsScreen.translations';
import { AchievementIcon } from './AchievementIcon';
import { AchievementRecentReceivers } from './AchievementRecentReceivers';
import { useClaimableRewards } from '../hooks/useClaimableRewards';
import { pickDescription, pickTitle, useAchievementLang } from '../utils/pickLocale';

const pct = (p: number) => (p < 1 ? p.toFixed(1) : Math.round(p));

export function AchievementModal({ achievementKey }: { achievementKey: string }) {
  const { t } = useLocalTranslation(translations);
  const lang = useAchievementLang();
  const openClaimSheet = useOpenClaimSheet();
  const { data: catalog, isLoading: catalogLoading } = useCatalog();
  const { data: mine } = useMyAchievements();
  const { data: claimable } = useClaimableRewards();
  // Stats 404 for anonymous viewers on hidden rules (enumeration defence).
  const { data: stats, error: statsError } = useAchievementStats(achievementKey, {
    enabled: !!catalog?.items.length,
  });

  if (catalogLoading || !catalog) {
    return <div className="p-6 text-center text-sm text-label-secondary">{t('loading')}</div>;
  }

  const item = catalog.items.find((i) => i.key === achievementKey);
  if (!item) return null;

  const mineItem = mine?.items.find((m) => m.key === achievementKey);
  const unlockedAt = mineItem?.unlockedAt ?? null;
  const progress = mineItem?.progress ?? null;
  // Earned but the coins/XP aren't credited yet. They unlock only once the
  // account's deposit covers the bonus (claimable.canClaim, account-wide). Both
  // cases open the claim sheet — it shows the claim button or the deposit
  // progress depending on coverage.
  const earnedUncredited = !!unlockedAt && !mineItem?.rewardClaimed;
  const depositCovered = claimable?.canClaim ?? false;
  const knownStats = stats && !(statsError instanceof AchievementStatsNotFoundError) ? stats : null;

  const title = pickTitle(item.i18n, lang);
  const description = pickDescription(item.i18n, lang);

  return (
    <div className="relative px-4 pb-8 pt-10">
      {/* Full-bleed ambient backdrop — blurred icon art tints the panel
          edge-to-edge (colour from the achievement itself). */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[320px] overflow-hidden rounded-t-[36px]">
        <div className="absolute inset-0 scale-125 opacity-40 blur-[60px]">
          <AchievementIcon iconFile={item.iconFile} alt="" fill className="object-contain" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_0%,transparent,rgba(20,20,20,0.85)_70%,#141414)]" />
      </div>

      <div className="relative flex flex-col items-center text-center">
        <div className="relative aspect-square w-[74%]">
          <AchievementIcon
            iconFile={item.iconFile}
            alt={title}
            fill
            className="relative object-contain drop-shadow-[0_12px_32px_rgba(0,0,0,0.6)]"
          />
        </div>

        <h2 className="mt-4 text-lg font-bold text-label-primary">{title}</h2>
        <p className="mt-1.5 max-w-[300px] text-sm leading-relaxed text-label-secondary">{description}</p>
        {knownStats && (
          <p className="mt-2 text-xs text-label-tertiary">{t('ownedByPercent', { percent: pct(knownStats.percentage) })}</p>
        )}

        <div className="mt-4 flex items-center gap-2">
          {!!item.reward.credits && (
            <span className="inline-flex items-center gap-1 rounded-full bg-fill-tertiary px-3.5 py-1.5 text-sm font-semibold text-amber-400">
              +<Credits amount={item.reward.credits} iconSize={13} />
            </span>
          )}
          {!!item.reward.xp && (
            <span className="rounded-full bg-fill-tertiary px-3.5 py-1.5 text-sm font-semibold text-label-primary">
              +{item.reward.xp} XP
            </span>
          )}
        </div>

        {progress && !unlockedAt && (
          <div className="mt-4 w-full max-w-[260px]">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-fill-tertiary">
              <div
                className="h-full rounded-full bg-amber-400"
                style={{ width: `${Math.min(100, (progress.value / progress.threshold) * 100)}%` }}
              />
            </div>
            <div className="mt-1.5 text-xs text-label-secondary">
              {t('progress')}: {progress.value} / {progress.threshold}
            </div>
          </div>
        )}

        {earnedUncredited && (
          <button
            type="button"
            onClick={openClaimSheet}
            className={`mt-5 w-full max-w-[300px] rounded-xl px-4 py-2.5 text-sm font-bold text-black transition ${
              depositCovered
                ? 'animate-shot-claim-glow bg-gradient-to-r from-amber-400 to-amber-500'
                : 'bg-amber-400'
            }`}
          >
            {depositCovered ? t('claimReward') : t('depositToUnlock')}
          </button>
        )}
      </div>

      <div className="mt-6">
        <AchievementRecentReceivers
          achievementKey={achievementKey}
          iconFile={item.iconFile}
          myUnlockedAt={unlockedAt}
        />
      </div>
    </div>
  );
}
